import {catchError, Observable, of, ReplaySubject, timer} from 'rxjs';

import {map, mergeMap, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {BlobId, ProjectId, VirtualPackageId} from '../types/ids';
import {EventsService, WebmateWebsocketEvent} from './events/events.service';
import {isNoUUID, isUUID} from '../utils/utils';
import {UserService} from './user.service';
import {PackageApi} from '../api/rest/package.restapi';
import {ReleaseMetaInfo, VirtualPackage} from '../types/packages/package';
import {WmNotificationService} from '../components/notification/wm-notification.service';

@Injectable({
    providedIn: 'root'
})
export class PackageService {

    /** delay before we ask the backend again for a Package that has not shown up in the published list yet */
    private static readonly PACKAGE_RECHECK_DELAY_MS = 2000;
    /** how often we recheck before giving up quietly (the websocket event may still fix it later) */
    private static readonly PACKAGE_RECHECK_ATTEMPTS = 3;

    //  _____ _   _ _______ ______ _____  _   _          _
    // |_   _| \ | |__   __|  ____|  __ \| \ | |   /\   | |
    //   | | |  \| |  | |  | |__  | |__) |  \| |  /  \  | |
    //   | | | . ` |  | |  |  __| |  _  /| . ` | / /\ \ | |
    //  _| |_| |\  |  | |  | |____| | \ \| |\  |/ ____ \| |____
    // |_____|_| \_|  |_|  |______|_|  \_\_| \_/_/    \_\______|

    private packagesCache: {[projectId: ProjectId]: ReplaySubject<VirtualPackage[]>} = {};

    private _packageEventsHandleByProject: { [s: string /* ProjectId*/]: Observable<WebmateWebsocketEvent> } = {}; //events

    constructor(private eventsService: EventsService, private packageApi: PackageApi, private userService: UserService, public notificationService: WmNotificationService) {
    }

    private pushToCache(projectId: ProjectId, packages: VirtualPackage[]): void {
        if (!this.packagesCache[projectId]) {
            this.packagesCache[projectId] = new ReplaySubject<VirtualPackage[]>(1);
        }
        this.packagesCache[projectId].next(packages);
    }

    /** Re-fetches the Packages of a Project and pushes them into the cache. In contrast to 'fetchPackages' this completes. */
    private refreshPackages(projectId: ProjectId): Observable<VirtualPackage[]> {
        return this.packageApi.getPackagesForProject(projectId).pipe(tap(packages => this.pushToCache(projectId, packages)));
    }

    /**
     * Re-fetches the Packages of a Project until the given Package appears in the (published) list. The backend only lists Packages once their Document has
     * been published, which may lag behind the creating request, so a single refresh can still return the old list. Never fails, the caller is not interested
     * in the outcome.
     */
    private refreshUntilPackageIsListed(projectId: ProjectId, packageId: VirtualPackageId, remainingAttempts = PackageService.PACKAGE_RECHECK_ATTEMPTS): Observable<VirtualPackage[]> {
        return this.refreshPackages(projectId).pipe(
            mergeMap(packages => {
                if (remainingAttempts <= 1 || packages.some(p => p.id === packageId)) {
                    return of(packages);
                }
                return timer(PackageService.PACKAGE_RECHECK_DELAY_MS).pipe(
                    mergeMap(() => this.refreshUntilPackageIsListed(projectId, packageId, remainingAttempts - 1))
                );
            }),
            catchError(() => of([]))
        );
    }

    private fetchPackages(projectId: ProjectId): Observable<VirtualPackage[]> {
        return this.refreshPackages(projectId).pipe(mergeMap(() => this.packagesCache[projectId].asObservable()))
    }

    private _handleEvents(e: WebmateWebsocketEvent): void {
        if (e === undefined) return;
        if (e.eventType === 'Package.Added') {
            this.refreshPackages(e.projectId).subscribe();
        } else if (e.eventType === 'Package.InstrumentationSuccess') {
            this.refreshPackages(e.projectId).subscribe();
        } else if (e.eventType === 'Package.InstrumentationFailed') {
            if (e.packageId !== undefined) {
                this.packageApi.getPackageById(e.packageId)
                    .subscribe(p => this.notificationService.toastWarningNotification(`Instrumentation for package ${p.name} failed`));
            } else {
                this.notificationService.toastWarningNotification(`Package instrumentation has failed`);
            }
        }
    }

    private _registerEventsForProject(projectId: ProjectId): void {
        if (isUUID(projectId) && this._packageEventsHandleByProject[projectId] === undefined) {
            this._packageEventsHandleByProject[projectId] = this.eventsService.getEventsForProject(projectId); //register events!
            this._packageEventsHandleByProject[projectId].subscribe(
                next => this._handleEvents(next)
            );
        }
    }

    //   _____ ______ _______      _______ _____ ______             _____ _____
    //  / ____|  ____|  __ \ \    / /_   _/ ____|  ____|      /\   |  __ \_   _|
    // | (___ | |__  | |__) \ \  / /  | || |    | |__        /  \  | |__) || |
    //  \___ \|  __| |  _  / \ \/ /   | || |    |  __|      / /\ \ |  ___/ | |
    //  ____) | |____| | \ \  \  /   _| || |____| |____    / ____ \| |    _| |_
    // |_____/|______|_|  \_\  \/   |_____\_____|______|  /_/    \_\_|   |_____|


    /**
     * Test is package source config is valid (this makes webmate perform networking towards the package source).
     */
    testPackageSourceConfig(config: any): Observable<any> {
        return this.packageApi.testPackageSourceConfig(config);
    }

    /** returns a new Observable listing all Packages for a specific Project */
    getPackagesForProject(projectId: ProjectId): Observable<Array<VirtualPackage>> {
        if (isNoUUID(projectId)) throw new ReferenceError('\'getPackageIdsForProject\' called with an illegal ID: \'' + projectId + '\'');

        this._registerEventsForProject(projectId);
        if (this.packagesCache[projectId]) {
            // hand out what we have immediately, but make sure a stale cache corrects itself
            this.refreshPackages(projectId).subscribe({error: () => {}});
            return this.packagesCache[projectId];
        }
        return this.fetchPackages(projectId);

    }

    createPackage(projectId: ProjectId, blobId: BlobId, name: string, extension: string): Observable<VirtualPackage> {
        // => Triggers Added Event, but we cannot rely on it (see 'refreshUntilPackageIsListed')
        return this.packageApi.createPackage(blobId, projectId, name, extension).pipe(
            mergeMap(pkg => this.refreshUntilPackageIsListed(projectId, pkg.id).pipe(map(() => pkg)))
        );
    }

    updatePackage(packageId: VirtualPackageId, blobId: BlobId, name: string, extension: string, projectId?: ProjectId): Observable<VirtualPackage> {
        // => Triggers Added Event, but we cannot rely on it. The Package is already listed, so a single refresh is all we can check for.
        return this.packageApi.updatePackage(packageId, blobId, name, extension).pipe(
            mergeMap(pkg => projectId ? this.refreshPackages(projectId).pipe(map(() => pkg), catchError(() => of(pkg))) : of(pkg))
        );
    }

    updatePackageMetaInfo(packageId: VirtualPackageId, name: string, projectId: ProjectId): Observable<any> {
        // => Only updates Document. Reload manually
        return this.packageApi.updatePackageMetaInfo$(packageId, name).pipe(mergeMap(_ => {
            return this.refreshPackages(projectId);
        }));
    }

    deletePackage(id: VirtualPackageId, projectId: ProjectId): Observable<any> {
        // => need to trigger udpate manually
        return this.packageApi.deletePackage(id).pipe(mergeMap((resp) => {
            return this.refreshPackages(projectId);
        }));
    }

    createPackageFromPackageSource(projectId: ProjectId, packageSourceConfig: any, name: string): Observable<VirtualPackage> {
        // => triggers Added Event, but we cannot rely on it (see 'refreshUntilPackageIsListed')
        return this.packageApi.createPackageFromPackageSource(packageSourceConfig, projectId, name).pipe(
            mergeMap(pkg => this.refreshUntilPackageIsListed(projectId, pkg.id).pipe(map(() => pkg)))
        );
    }

    updatePackageFromPackageSource(packageId: VirtualPackageId, optRelease?: string, projectId?: ProjectId): Observable<any> {
        // => triggers Added Event, but we cannot rely on it. The Package is already listed, so a single refresh is all we can check for.
        return this.packageApi.updatePackageFromPackageSource(packageId, optRelease).pipe(
            mergeMap(resp => projectId ? this.refreshPackages(projectId).pipe(map(() => resp), catchError(() => of(resp))) : of(resp))
        );
    }

    getReleasesFromPackageSourceForPackage(packageId: VirtualPackageId): Observable<Array<ReleaseMetaInfo>> {
        return this.packageApi.getReleasesForPackageRepositoryOfPackage(packageId);
    }

}
